// FILE LOCATION: src/pages/Redeem.tsx
// ──────────────────────────────────────────────────────────────
// Magic-link redemption page. Reached via /r/:code.
//
// Flow:
//  - logged out → stash code in sessionStorage and redirect to Clerk
//                 sign-up. After sign-in, this page is reached again
//                 (now authenticated) and the redemption proceeds.
//  - logged in  → call redeem-access-grant edge function. Show
//                 success state with expiration date and CTA to
//                 dashboard.
// ──────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  useUser,
  SignedIn,
  SignedOut,
  RedirectToSignIn,
} from "@clerk/clerk-react";

const SUPABASE_FUNCTIONS_URL = `${
  import.meta.env.VITE_SUPABASE_URL
}/functions/v1`;

const STASH_KEY = "peerprep:pending_redeem_code";

type Status =
  | { kind: "idle" }
  | { kind: "redeeming" }
  | {
      kind: "success";
      tier: string;
      expiresAt: string;
      alreadyRedeemed: boolean;
    }
  | { kind: "error"; message: string };

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function RedeemInner({ code }: { code: string }) {
  const { user } = useUser();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const startedRef = useRef(false);

  useEffect(() => {
    if (!user || startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;

    async function redeem() {
      setStatus({ kind: "redeeming" });

      try {
        const res = await fetch(
          `${SUPABASE_FUNCTIONS_URL}/redeem-access-grant`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({ code, user_id: user!.id }),
          }
        );

        const data = await res.json();

        if (cancelled) return;

        if (!res.ok) {
          const messages: Record<string, string> = {
            invalid_code: "This code isn't valid.",
            code_expired: "This code has expired.",
            code_exhausted: "This code has reached its redemption limit.",
          };
          setStatus({
            kind: "error",
            message:
              messages[data.error] ||
              data.message ||
              data.error ||
              "Something went wrong.",
          });
          return;
        }

        setStatus({
          kind: "success",
          tier: data.granted_tier,
          expiresAt: data.expires_at,
          alreadyRedeemed: data.already_redeemed === true,
        });

        // Clear any stashed code on success.
        sessionStorage.removeItem(STASH_KEY);
      } catch (err: any) {
        if (cancelled) return;
        setStatus({
          kind: "error",
          message: err.message || "Network error",
        });
      }
    }

    redeem();

    return () => {
      cancelled = true;
    };
  }, [user, code]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0e1117",
        padding: "24px",
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: "100%",
          background: "#161b22",
          border: "1px solid #2dd4bf33",
          borderRadius: 12,
          padding: "32px",
          color: "#e6edf3",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {status.kind === "idle" || status.kind === "redeeming" ? (
          <>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>
              Activating your access…
            </h1>
            <p style={{ marginTop: 12, color: "#8b949e", fontSize: 14 }}>
              One moment while we redeem code{" "}
              <code style={codeStyle}>{code}</code>.
            </p>
          </>
        ) : null}

        {status.kind === "success" ? (
          <>
            <div style={{ fontSize: 28 }}>✓</div>
            <h1 style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600 }}>
              {status.alreadyRedeemed
                ? "You already have this access"
                : "Access granted"}
            </h1>
            <p
              style={{
                marginTop: 12,
                color: "#8b949e",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              You now have PeerPrep{" "}
              <strong style={{ color: "#2dd4bf" }}>{status.tier}</strong> access
              through <strong>{formatDate(status.expiresAt)}</strong>.
            </p>
            <button onClick={() => navigate("/")} style={primaryButtonStyle}>
              Go to dashboard
            </button>
          </>
        ) : null}

        {status.kind === "error" ? (
          <>
            <div style={{ fontSize: 28, color: "#ef4444" }}>✕</div>
            <h1 style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600 }}>
              Couldn't redeem
            </h1>
            <p
              style={{
                marginTop: 12,
                color: "#8b949e",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              {status.message}
            </p>
            <p style={{ marginTop: 16, color: "#6e7681", fontSize: 13 }}>
              Code: <code style={codeStyle}>{code}</code>
            </p>
            <Link
              to="/"
              style={{
                ...primaryButtonStyle,
                textDecoration: "none",
                display: "inline-block",
              }}
            >
              Go home
            </Link>
          </>
        ) : null}
      </div>
    </div>
  );
}

const codeStyle: React.CSSProperties = {
  background: "#0d1117",
  padding: "2px 8px",
  borderRadius: 4,
  border: "1px solid #30363d",
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: 13,
  color: "#2dd4bf",
};

const primaryButtonStyle: React.CSSProperties = {
  marginTop: 24,
  background: "#2dd4bf",
  color: "#0e1117",
  border: "none",
  borderRadius: 8,
  padding: "10px 20px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

export default function Redeem() {
  const { code } = useParams<{ code: string }>();

  if (!code) {
    return (
      <div
        style={{
          padding: 40,
          color: "#e6edf3",
          background: "#0e1117",
          minHeight: "100vh",
        }}
      >
        No code provided.
      </div>
    );
  }

  return (
    <>
      <SignedIn>
        <RedeemInner code={code} />
      </SignedIn>
      <SignedOut>
        <StashAndSignIn code={code} />
      </SignedOut>
    </>
  );
}

// When logged out, stash the code in sessionStorage so we can pick it up
// after sign-in and complete the redemption automatically.
function StashAndSignIn({ code }: { code: string }) {
  useEffect(() => {
    sessionStorage.setItem(STASH_KEY, code);
  }, [code]);

  return <RedirectToSignIn redirectUrl={`/r/${code}`} />;
}